import styled, { keyframes } from "styled-components";
import color from "../../resource/color";
import { GreyTableContainer } from "../../components/Table/GreyTable";

export const AlignTopTableContainer = styled(GreyTableContainer)`
  .ant-table-tbody > tr > td {
    vertical-align: top;
  }
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    white-space: nowrap;
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
`;

export const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

export const KpiChip = styled.button<{ $bg: string; $active: boolean }>`
  position: relative;
  overflow: hidden;
  border: none;
  border-radius: 16px;
  padding: 14px 16px;
  text-align: left;
  cursor: pointer;
  background: ${(props) => props.$bg};
  color: #fff;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  /* Selected: thin white gap hugging the chip, then a navy ring. Done with box-shadow (not
     outline) so the ring follows the 16px corners. */
  box-shadow: ${(props) => (props.$active ? `0 0 0 2px ${color.white}, 0 0 0 4px ${color.secondary}` : "none")};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.$active
        ? `0 0 0 2px ${color.white}, 0 0 0 4px ${color.secondary}, 0 6px 16px rgba(0, 0, 0, 0.18)`
        : "0 6px 16px rgba(0, 0, 0, 0.18)"};
  }
`;

const kpiShimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

/**
 * Placeholder chip shown while the tier overview loads (each filter change is a new query
 * with no data yet). Same padding/radius as KpiChip and line boxes sized like KpiValue /
 * KpiTitle / KpiSub, so the row keeps its height instead of collapsing and jumping.
 */
export const KpiSkeleton = styled.div`
  border-radius: 16px;
  padding: 14px 16px;
  background: ${color.background1};
`;

export const KpiSkeletonLine = styled.div<{ $width: string; $height: number; $gap?: number }>`
  width: ${(props) => props.$width};
  height: ${(props) => props.$height}px;
  margin-top: ${(props) => props.$gap ?? 0}px;
  border-radius: 6px;
  background: linear-gradient(90deg, ${color.border} 25%, ${color.white} 50%, ${color.border} 75%);
  background-size: 200% 100%;
  animation: ${kpiShimmer} 1.2s ease-in-out infinite;
`;

export const KpiValue = styled.div<{ $color?: string }>`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: ${(props) => props.$color ?? "inherit"};
`;

export const KpiUnit = styled.span`
  font-size: 13px;
  font-weight: 400;
  margin-left: 4px;
  opacity: 0.85;
`;

/**
 * Text color for a tier chip: dark grey when the background's perceived brightness
 * (0.299·R + 0.587·G + 0.114·B, 0-255) is above 140, white otherwise. Accepts "#RGB" /
 * "#RRGGBB" (alpha ignored); anything unparsable keeps the white default.
 */
export const kpiTextColorBoolean = (bg?: string | null): boolean => {
  if (!bg) return false;
  let hex = bg.trim().replace(/^#/, "");
  if (hex.length === 3 || hex.length === 4) hex = hex.replace(/./g, "$&$&");
  if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex)) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
  return brightness > 140;
};

export const kpiTextColor = (bg?: string | null): string =>
  kpiTextColorBoolean(bg) ? "#484e5e" : "#fff";

export const KpiTitle = styled.div<{ $color?: string }>`
  font-size: 15px;
  font-weight: 700;
  margin-top: 6px;
  color: ${(props) => props.$color ?? "inherit"};
`;

export const KpiSub = styled.div<{ $color?: string }>`
  font-size: 12px;
  opacity: 0.9;
  margin-top: 2px;
  color: ${(props) => props.$color ?? "inherit"};
`;

export const NavWarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${color.background1};
  border: 1px solid ${color.border};
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

export const NavWarningLink = styled.a`
  color: ${color.primary};
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    color: ${color.softPrimary};
  }
`;

export const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const CountRow = styled.div`
  text-align: right;
  color: ${color.Text3};
  font-size: 13px;
  margin-bottom: 8px;
`;

export const DetailTopbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const BackButton = styled.button`
  border: 1px solid ${color.border};
  background: #fff;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${color.Text1};

  &:hover {
    border-color: ${color.primary};
    color: ${color.primary};
  }
`;

export const MonitorCard = styled.div`
  border: 1px solid ${color.border};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`;

export const MonitorFacts = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${color.background2};
  overflow: hidden;
`;

export const ProgressBar = styled.div<{ $percent: number, $color : string }>`
  height: 100%;
  width: ${(props) => Math.min(Math.max(props.$percent, 0), 100)}%;
  background: ${(props) => props.$color};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

export const ProgressScale = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${color.Text3};
  margin-top: 6px;
`;

export const NavCard = styled.div`
  border: 1px solid ${color.border};
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

export const BenefitSection = styled.div`
  margin-bottom: 20px;
`;

export const BenefitList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  color: ${color.Text1};

  li {
    margin-bottom: 4px;
  }
`;

export const AuditChangeList = styled.ul`
  margin: 2px 0 6px;
  padding-left: 20px;
  color: ${color.Text3};

  li {
    margin-bottom: 2px;
  }
`;

export const SectionHead = styled.div`
  font-weight: 700;
  color: ${color.Text2};
  margin-bottom: 8px;
`;
